import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  MapPin,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertTriangle,
  Home,
  Building2,
  Activity,
  Target,
  Shield,
  Eye,
  Filter,
  Layers
} from 'lucide-react';

interface ZoneData {
  id: string;
  name: string;
  population: number;
  density: 'very-high' | 'high' | 'medium' | 'low';
  evacuated: number;
  remaining: number;
  priority: number;
  risk: 'critical' | 'high' | 'medium' | 'low';
  buildings: number;
  area: string;
}

interface DensityPoint {
  x: number;
  y: number;
  intensity: number;
  population: number;
}

const PopulationHeatmap: React.FC = () => {
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'density' | 'evacuation' | 'risk'>('density');

  const zones: ZoneData[] = [
    {
      id: 'A',
      name: 'Zone A',
      population: 12470,
      density: 'very-high',
      evacuated: 8920,
      remaining: 3550,
      priority: 1,
      risk: 'critical',
      buildings: 234,
      area: '2.4 km²'
    },
    {
      id: 'B',
      name: 'Zone B',
      population: 23450,
      density: 'high',
      evacuated: 15670,
      remaining: 7780,
      priority: 2,
      risk: 'high',
      buildings: 456,
      area: '3.8 km²'
    },
    {
      id: 'C',
      name: 'Zone C',
      population: 18900,
      density: 'medium',
      evacuated: 12340,
      remaining: 6560,
      priority: 3,
      risk: 'medium',
      buildings: 345,
      area: '3.2 km²'
    },
    {
      id: 'D',
      name: 'Zone D',
      population: 34560,
      density: 'high',
      evacuated: 23450,
      remaining: 11110,
      priority: 4,
      risk: 'medium',
      buildings: 567,
      area: '4.5 km²'
    },
    {
      id: 'E',
      name: 'Zone E',
      population: 15670,
      density: 'low',
      evacuated: 10980,
      remaining: 4690,
      priority: 5,
      risk: 'low',
      buildings: 234,
      area: '2.8 km²'
    }
  ];

  // Simulated heatmap data points
  const heatmapPoints: DensityPoint[] = [
    { x: 30, y: 40, intensity: 0.9, population: 1250 },
    { x: 45, y: 35, intensity: 0.8, population: 980 },
    { x: 60, y: 55, intensity: 0.7, population: 760 },
    { x: 25, y: 65, intensity: 0.6, population: 540 },
    { x: 70, y: 30, intensity: 0.5, population: 430 },
    { x: 55, y: 70, intensity: 0.4, population: 320 },
    { x: 40, y: 50, intensity: 0.3, population: 210 },
    { x: 75, y: 60, intensity: 0.2, population: 150 },
    { x: 20, y: 25, intensity: 0.1, population: 80 },
  ];

  const getDensityColor = (density: string) => {
    switch(density) {
      case 'very-high': return 'bg-red-500 border-red-500';
      case 'high': return 'bg-orange-500 border-orange-500';
      case 'medium': return 'bg-yellow-500 border-yellow-500';
      case 'low': return 'bg-green-500 border-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getRiskColor = (risk: string) => {
    switch(risk) {
      case 'critical': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-green-500 border-green-500 bg-green-500/10';
      default: return 'text-gray-500';
    }
  };

  const getIntensityColor = (intensity: number) => {
    if (intensity > 0.8) return 'bg-red-500';
    if (intensity > 0.6) return 'bg-orange-500';
    if (intensity > 0.4) return 'bg-yellow-500';
    if (intensity > 0.2) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const totalPopulation = zones.reduce((sum, zone) => sum + zone.population, 0);
  const totalEvacuated = zones.reduce((sum, zone) => sum + zone.evacuated, 0);
  const totalRemaining = zones.reduce((sum, zone) => sum + zone.remaining, 0);

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="text-yellow-500 animate-pulse" />
            POPULATION DENSITY OVERLAY
          </h2>
          <p className="text-xs text-gray-400 mt-1">Real-time population distribution</p>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('density')}
            className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1 ${
              viewMode === 'density'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-800 text-gray-400'
            }`}
          >
            <Layers size={14} />
            Density
          </button>
          <button
            onClick={() => setViewMode('evacuation')}
            className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1 ${
              viewMode === 'evacuation'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-800 text-gray-400'
            }`}
          >
            <Target size={14} />
            Evacuation
          </button>
          <button
            onClick={() => setViewMode('risk')}
            className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1 ${
              viewMode === 'risk'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-800 text-gray-400'
            }`}
          >
            <AlertTriangle size={14} />
            Risk
          </button>
        </div>
      </div>

      {/* Heatmap Visualization */}
      <div className="relative bg-black/50 rounded-lg p-4 mb-6 border border-cyan-500/30 h-64">
        {/* Grid Background */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} />

        {/* Heatmap Points */}
        {heatmapPoints.map((point, idx) => (
          <motion.div
            key={idx}
            className="absolute cursor-pointer group"
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
            initial={{ scale: 0 }}
            animate={{ scale: point.intensity * 2 }}
            transition={{ duration: 0.5, delay: idx * 0.05 }}
          >
            <div className={`w-12 h-12 rounded-full ${getIntensityColor(point.intensity)} opacity-40 group-hover:opacity-60 transition-all`}
                 style={{ transform: 'translate(-50%, -50%)' }} />
            <div className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full" />
            
            {/* Tooltip */}
            <div className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-8 bg-black/90 border border-cyan-500 rounded px-2 py-1 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
              {point.population} people
            </div>
          </motion.div>
        ))}

        {/* Zone Labels */}
        {zones.map((zone) => (
          <div
            key={zone.id}
            className="absolute text-xs font-bold text-gray-400 cursor-pointer hover:text-cyan-500 transition-colors"
            style={{ 
              left: `${20 + (parseInt(zone.id.charCodeAt(0).toString()) % 5) * 15}%`, 
              top: `${30 + (parseInt(zone.id) * 7) % 50}%` 
            }}
            onClick={() => setSelectedZone(zone.id)}
          >
            {zone.name}
          </div>
        ))}
      </div>

      {/* Zone Statistics */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scroll">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <MapPin size={14} className="text-cyan-500" />
          Zone-wise Population Data
        </h3>

        {zones.map((zone) => (
          <motion.div
            key={zone.id}
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`bg-black/30 rounded-lg p-4 border transition-all cursor-pointer ${
              selectedZone === zone.id
                ? 'border-cyan-500 bg-cyan-500/10'
                : 'border-gray-700 hover:border-cyan-500/50'
            }`}
            onClick={() => setSelectedZone(zone.id === selectedZone ? null : zone.id)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getDensityColor(zone.density)}`} />
                <h4 className="font-bold">{zone.name}</h4>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${getRiskColor(zone.risk)}`}>
                {zone.risk.toUpperCase()} RISK
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-black/50 p-2 rounded text-center">
                <div className="text-lg font-bold text-cyan-500">{(zone.population / 1000).toFixed(1)}k</div>
                <div className="text-[10px] text-gray-400">Total</div>
              </div>
              <div className="bg-black/50 p-2 rounded text-center">
                <div className="text-lg font-bold text-green-500">{(zone.evacuated / 1000).toFixed(1)}k</div>
                <div className="text-[10px] text-gray-400">Evacuated</div>
              </div>
              <div className="bg-black/50 p-2 rounded text-center">
                <div className="text-lg font-bold text-red-500">{(zone.remaining / 1000).toFixed(1)}k</div>
                <div className="text-[10px] text-gray-400">Remaining</div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <Building2 size={12} />
                {zone.buildings} buildings
              </div>
              <div className="flex items-center gap-1">
                <Activity size={12} />
                {zone.area}
              </div>
              <div className="flex items-center gap-1">
                <Target size={12} />
                Priority {zone.priority}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">Evacuation Progress</span>
                <span className="text-cyan-500">
                  {Math.round((zone.evacuated / zone.population) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(zone.evacuated / zone.population) * 100}%` }}
                  className="h-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                />
              </div>
            </div>

            {/* Expanded Details */}
            {selectedZone === zone.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                className="mt-3 pt-3 border-t border-gray-700"
              >
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-black/50 rounded">
                    <p className="text-[10px] text-gray-400">Population Density</p>
                    <p className="text-sm font-bold capitalize">{zone.density.replace('-', ' ')}</p>
                  </div>
                  <div className="p-2 bg-black/50 rounded">
                    <p className="text-[10px] text-gray-400">People per km²</p>
                    <p className="text-sm font-bold">{Math.round(zone.population / parseFloat(zone.area)).toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-black/50 rounded">
                    <p className="text-[10px] text-gray-400">Evacuation Priority</p>
                    <p className="text-sm font-bold text-red-500">{zone.priority === 1 ? 'IMMEDIATE' : `Level ${zone.priority}`}</p>
                  </div>
                  <div className="p-2 bg-black/50 rounded">
                    <p className="text-[10px] text-gray-400">Risk Assessment</p>
                    <p className={`text-sm font-bold ${getRiskColor(zone.risk).split(' ')[0]}`}>
                      {zone.risk.toUpperCase()}
                    </p>
                  </div>
                </div>

                <button className="w-full mt-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold py-2 px-3 rounded-lg text-sm">
                  DEPLOY RESOURCES TO {zone.name}
                </button>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center pt-4 border-t border-gray-700/50">
        <div>
          <div className="text-2xl font-bold text-cyan-500">{(totalPopulation / 1000).toFixed(1)}k</div>
          <div className="text-xs text-gray-400">Total Population</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-500">{(totalEvacuated / 1000).toFixed(1)}k</div>
          <div className="text-xs text-gray-400">Evacuated</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-red-500">{(totalRemaining / 1000).toFixed(1)}k</div>
          <div className="text-xs text-gray-400">Remaining</div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span className="text-gray-400">Very High</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-orange-500 rounded" />
          <span className="text-gray-400">High</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-yellow-500 rounded" />
          <span className="text-gray-400">Medium</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span className="text-gray-400">Low</span>
        </div>
      </div>
    </div>
  );
};

export default PopulationHeatmap;